import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, X, ArrowRight } from 'lucide-react';

/**
 * Guest-checkout modal shown when an anonymous visitor hits "Place Order".
 *
 * Collects just an email (no password) → calls /api/auth/guest-signup →
 * auth context is updated with an is_guest:true user → parent's onContinue
 * is re-invoked so the order submission proceeds without a full signup.
 *
 * Users with existing full accounts get a 409 back from the backend; we
 * show that message and route them to /login with the email prefilled.
 */
export const GuestCheckoutPrompt = ({ open, onClose, onContinue }) => {
  const navigate = useNavigate();
  const { guestSignup } = useAuth();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  if (!open) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const r = await guestSignup({
      email: email.trim().toLowerCase(),
      first_name: firstName.trim(),
      last_name: lastName.trim(),
    });
    setBusy(false);

    if (!r.success) {
      if (r.status === 409) {
        setError(`${r.error} Redirecting you to sign in…`);
        setTimeout(() => {
          onClose?.();
          navigate('/login', { state: { prefilledEmail: email.trim().toLowerCase() } });
        }, 1400);
        return;
      }
      setError(r.error || 'Could not continue as guest.');
      return;
    }

    // Close the modal BEFORE firing onContinue so any downstream validation
    // alerts (e.g. incomplete shipping address) don't leave the guest form
    // stuck on screen.
    onClose?.();
    onContinue?.();
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      data-testid="guest-checkout-modal"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="guest-checkout-close-btn"
          aria-label="Close"
          className="absolute top-3 right-3 p-1 text-gray-400 hover:text-gray-700"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <Mail className="w-5 h-5 text-[#4A7C59]" />
          <h3 className="text-lg font-bold text-gray-800">Continue as guest</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Drop your email and we'll save this order for you. No password yet — you can claim your account after checkout for 1-click reorders.
        </p>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label htmlFor="guest-email" className="text-xs font-semibold text-gray-700">
              Email address
            </Label>
            <Input
              id="guest-email"
              data-testid="guest-checkout-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@company.com"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label htmlFor="guest-first" className="text-xs font-semibold text-gray-700">
                First name
              </Label>
              <Input
                id="guest-first"
                data-testid="guest-checkout-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
                maxLength={60}
              />
            </div>
            <div>
              <Label htmlFor="guest-last" className="text-xs font-semibold text-gray-700">
                Last name
              </Label>
              <Input
                id="guest-last"
                data-testid="guest-checkout-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                maxLength={60}
              />
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600" data-testid="guest-checkout-error">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={busy || !email}
            data-testid="guest-checkout-submit"
            className="w-full bg-[#4A7C59] hover:bg-[#3d6849] text-white flex items-center justify-center gap-2"
          >
            {busy ? 'One moment…' : (<>Continue to order <ArrowRight className="w-4 h-4" /></>)}
          </Button>

          <p className="text-[11px] text-gray-500 text-center">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => {
                onClose?.();
                navigate('/login');
              }}
              data-testid="guest-checkout-login-link"
              className="text-[#4A7C59] hover:underline"
            >
              Sign in instead
            </button>
          </p>
        </form>
      </div>
    </div>
  );
};
